// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/CommentContextChip.test.tsx
import { Flex, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type AskV2CommentContext } from "./types";

import { AskPunttReviewCommentBadge } from "../../components/ask-puntt-review";
import {
  normalizeReviewCommentTag,
  reviewCommentTagLabelKey,
} from "../review/reviewCommentTags";

type CommentContextChipProps = {
  context: AskV2CommentContext;
  testId: string;
  onRemove?: () => void;
  /** Full-width inset strip at the top of the v2 composer (not sent-message chips). */
  variant?: "default" | "composer";
};

export function CommentContextChip({
  context,
  testId,
  onRemove,
  variant = "default",
}: CommentContextChipProps) {
  const { t } = useTranslation("ui");
  const normalizedTag =
    context.tag != null ? normalizeReviewCommentTag(context.tag) : null;
  const tagLabel =
    normalizedTag != null ? t(reviewCommentTagLabelKey(normalizedTag)) : null;

  const isComposer = variant === "composer";

  return (
    <Flex
      align="center"
      gap="2"
      className={
        isComposer
          ? "w-full rounded-lg border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-3 p-2"
          : "max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
      }
      data-testid={testId}
    >
      <AskPunttReviewCommentBadge number={context.number} />
      <div className="min-w-0 flex-1">
        <Text
          size="1"
          className={
            isComposer
              ? "block truncate font-medium text-puntt-neutral-gray-12"
              : "block max-w-[180px] truncate font-medium text-puntt-neutral-gray-12"
          }
        >
          {context.title}
        </Text>
        {tagLabel ? (
          <Text size="1" className="text-puntt-neutral-gray-9">
            {tagLabel}
          </Text>
        ) : null}
      </div>
      {onRemove ? (
        <button
          type="button"
          data-testid={`ask-v2-remove-comment-context-${context.id}`}
          onClick={onRemove}
          className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
          aria-label={t("askV2.transport.removeCommentContext")}
        >
          ×
        </button>
      ) : null}
    </Flex>
  );
}
